import { SafeUrl } from '@angular/platform-browser';
import { Dan, ProfileDto, Stamp } from '@escrowtab/v2profile-api-client';
import {
  AssociatedCompaniesEntityOrCompany,
  DevicesEntity,
  SkillsEntity,
} from './admin-user.interface';

export interface Profile extends ProfileDto {
  stackOrder?: string | null;
  eJournalCustodianEmail?: string | null;
  eJournalCustodianName?: string | null;
  /** Not yet present in the generated @escrowtab/v2profile-api-client ProfileDto typings
   * (published separately from this repo) — remove this override once that package is
   * republished with IsEtAdmin. */
  isEtAdmin?: boolean;
}

export enum ProfileUserTypeEnum {
  'Notary-Signing-Agent' = 1,
  'Notary-Notary' = 2,
  'Lender-Loan-Officer' = 3,
  'Lender-Closing-Department' = 4,
  'Title-Signing-Agent' = 5,
  'Title' = 6,
  'Signor' = 7,
  'Closing-Company' = 8,
}

export interface AADUserWithClaims {
  homeAccountId: string;
  environment: string;
  tenantId: string;
  username: string;
  localAccountId: string;
  name: string;
  idTokenClaims: Partial<IDTokenClaims>;
}

export interface IDTokenClaims {
  exp: number;
  nbf: number;
  ver: string;
  iss: string;
  sub: string;
  aud: string;
  acr: string;
  nonce: string;
  iat: number;
  auth_time: number;
  oid: string;
  name: string;
  extension_et_user_type: string;
  extension_et_profile_id: string;
  extension_et_company_id: string;
  extension_et_is_company_admin: boolean;
  emails: string[];
  at_hash: string;
}

export interface EJournalBody {
  id?: string;
  state: string;
  feeCharged: number;
  utcCreatedOn: Date;
  thirdPartyId?: 'Stripe';
  thirdPartyVerificationId?: string;
  profileId: string;
  packetId: string;
  signers: EJournalBodySigner[];
  data?: string;
}

interface EJournalBodySigner {
  name?: string;
  state?: string;
  idNumber?: string;
  idType?: string;
  address?: string;
  signatureImg?: string;
  utcIdIssuedOn?: string;
  utcIdExpiresOn?: string;
}

export interface ETJournalAPIResponse {
  data: EJournalBody[] | EJournalBody;
  message: string;
  modelErrors: string;
  status: boolean;
  statusCode: number;
}

export interface EJournalEntry extends EJournalBody {
  documentName: string;
  notaryName: string;
  /** Packet/document name from API (e.g. new eJournal contract); used as fallback for documentName */
  packetName?: string;
  signersCount: number;
  stateAbbreviation: string;
  stamps?: number;
  stampsDetails: EJournalEntryStampDetails[];
}

export interface EJournalEntryStampDetails {
  page?: number;
  type?: string;
  description: string;
  ['notarial-act']?: string;
  dan?: Dan;
}

export interface StampWithImage extends Stamp {
  image: string | SafeUrl | null;
}

export interface CompanyResponse {
  statusCode: number;
  status: boolean;
  message: any;
  data: CompanyData;
  modelErrors: any;
}

export interface CompanyData {
  companyId: string;
  name: string;
  emailDomain: string;
  companyType: number;
  isActive: boolean;
  enoteEnabled: boolean;
  elsiEnabled: boolean;
  autoTagOnboarding: boolean;
  hqState: any;
  iDverify: boolean;
  stackingEnabled: boolean;
  requestStampEnabled: boolean;
  mfaEnabled: boolean;
}

export interface StackingOrderResponse {
  statusCode: number;
  status: boolean;
  message: any;
  data: StackingOrder | null;
  modelErrors: any;
}

export interface StackingOrder {
  buyers: StackingItem[];
  sellers: StackingItem[];
  both: StackingItem[];
}

export interface StackingItem {
  pageType: string;
  stackingId: string;
}

export interface StackingItemWithSubset extends StackingItem {
  userSubsets?: UserSubsets;
}

export interface SubsetsResponse {
  statusCode: number;
  status: boolean;
  message: any;
  data: SubsetData;
  modelErrors: any;
}

export interface SubsetData {
  companyId: string;
  companyName: string;
  pageSubset: Subset[];
}

export interface Subset {
  subsetId: string;
  name: string;
}

export interface UserSubsetsData {
  profileId: string;
  companyId: string;
  pageSubset: UserSubsets[];
}

export interface UserSubsets {
  stackingId: string;
  stackingSubset: Subset[];
}

export interface AddSubsetsDto {
  companyId: string;
  companyName: string;
  pageSubset: string[];
}

export type UpdateSubsetsDto = Array<{ key: string; value: string }>;

export interface UpsertSubsetsResponse {
  statusCode: number;
  status: boolean;
  message: any;
  data: string;
  modelErrors: any;
}

export interface SelfProfileApiResponse {
  statusCode: number;
  status: boolean;
  message: any;
  data: SelfProfile;
  modelErrors: any;
}

export interface SelfProfile {
  profileId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  userType: number;
  isCompanyAdmin: boolean;
  /** System-wide EscrowTab staff admin flag (unlike isCompanyAdmin, not scoped to the
   * profile's own company). Grants access to the internal admin app. */
  isEtAdmin: boolean;
  isActive: boolean;
  isPremium: boolean;
  hasTablet: boolean;
  signingCount: number;
  rating: number;
  serviceAreaZipCode: string;
  servicingDistanceInMiles: string;
  // TODO(security): confirm whether PIN should be returned to client at all.
  pin: string | null;
  authType: string;
  preferredFirstFactor?: string;
  preferredMfaChannel?: string;
  /** 'None' | 'Email' | 'Sms' | 'Authenticator' — admin-set required 2FA method for this
   * profile. Combined with the Better Auth session's `user.twoFactorEnabled` by the
   * mfa-required guard to decide whether to block the app until the user completes
   * real TOTP enrollment (see docs/tasks mandatory-2FA-enrollment plan). */
  requiredMfaMethod?: 'None' | 'Email' | 'Sms' | 'Authenticator';
  fileShareID: string;
  personalCertificate: string;
  legacyProfileId: string;
  utcCreatedOn: Date;
  utcModifiedOn: Date;
  company: AssociatedCompaniesEntityOrCompany;
  associatedCompanies: AssociatedCompaniesEntityOrCompany[];
  skills: SkillsEntity[];
  devices: DevicesEntity[];
  stackOrder: string;
  eJournalCustodianEmail: string | null;
  eJournalCustodianName: string | null;
}

export interface ProfileName {
  profileId: string;
  firstName: string;
  lastName: string;
  companyId: string;
}
